import { useEffect, useState } from 'react';
import { useCrmStore } from '../store/crm.store';
import { CrmField } from '../types';
import { Locator } from '@/features/application/types/forms';
import {
  isFilterable,
  getAdaptedInputType,
  isSortable,
  AdaptedInputType,
} from '../utils/field-metadata.utils';

interface UseLocatorBuilderProps {
  objectType: string;
  initialMode?: 'fieldValue' | 'positional';
  initialFieldApiName?: string;
  initialValue?: string | boolean;
  initialSortField?: string;
  initialSortDirection?: 'DESC' | 'ASC';
  initialIndex?: number;
}

export const useLocatorBuilder = ({
  objectType,
  initialMode = 'fieldValue',
  initialFieldApiName = '',
  initialValue = '',
  initialSortField = '',
  initialSortDirection = 'DESC',
  initialIndex = 0,
}: UseLocatorBuilderProps) => {
  const { fetchCrmFieldsForObject, getFieldsForObject, isLoadingFieldsForObject } = useCrmStore();

  const [mode, setMode] = useState<'fieldValue' | 'positional'>(initialMode);
  const [fields, setFields] = useState<CrmField[]>([]);
  const [selectedFieldApiName, setSelectedFieldApiName] = useState(initialFieldApiName);
  const [selectedValue, setSelectedValue] = useState<string | boolean>(initialValue);
  const [sortField, setSortField] = useState(initialSortField);
  const [sortDirection, setSortDirection] = useState<'DESC' | 'ASC'>(initialSortDirection);
  const [index, setIndexState] = useState(Math.max(0, initialIndex));
  const setIndex = (val: number) => setIndexState(Math.max(0, val));

  const isLoading = isLoadingFieldsForObject(objectType);

  useEffect(() => {
    fetchCrmFieldsForObject(objectType);
  }, [objectType, fetchCrmFieldsForObject]);

  useEffect(() => {
    const f = getFieldsForObject(objectType) || [];
    setFields(f);
  }, [objectType, getFieldsForObject, isLoading]);

  useEffect(() => {
    const sortable = fields.filter(isSortable);
    if (sortable.length && !sortField) {
      const created = sortable.find((f) => f.id === 'CreatedDate');
      setSortField(created ? created.id : sortable[0].id);
    }
  }, [fields, sortField]);

  const selectedField = fields.find((f) => f.id === selectedFieldApiName);
  const adaptedType: AdaptedInputType = selectedField ? getAdaptedInputType(selectedField) : 'text';

  const filterableFields = fields.filter(isFilterable);
  const sortableFields = fields.filter(isSortable);

  const canSaveFieldValue = !!selectedField && selectedValue !== '' && selectedValue !== null;

  const handleSaveFieldValue = (onSave: (locator: Locator) => void) => {
    if (!selectedField) return;

    const locator: Locator = {
      strategy: 'fieldValue',
      params: {
        [selectedField.id]: selectedValue,
      },
    };

    onSave(locator);
  };

  const handleSavePositional = (onSave: (locator: Locator) => void) => {
    const locator: Locator = {
      strategy: 'positional',
      params: {
        index,
        ...(sortField && { orderBy: sortField }),
        ...(sortField && { direction: sortDirection }),
      },
    };

    onSave(locator);
  };

  const canSave = mode === 'fieldValue' ? canSaveFieldValue : !!sortField;

  const handleSave = (onSave: (locator: Locator) => void) => {
    if (mode === 'fieldValue') {
      handleSaveFieldValue(onSave);
    } else {
      handleSavePositional(onSave);
    }
  };

  return {
    // State
    mode,
    fields,
    selectedFieldApiName,
    selectedValue,
    sortField,
    sortDirection,
    index,
    isLoading,
    selectedField,
    adaptedType,
    filterableFields,
    sortableFields,
    canSave,

    // State setters
    setMode,
    setSelectedFieldApiName,
    setSelectedValue,
    setSortField,
    setSortDirection,
    setIndex,

    // Handlers
    handleSave,
  };
};
